import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { authKeys } from "./auth";
import { apiFetch } from "./client";
import { refusalFrom } from "./refusal";

/** The roles whose second factor is a rule rather than a choice (`identity.MFA_REQUIRED_ROLES`, ADR-0008;
 * the Auditor joined on 2026-11-03, ADR-0227). The server sends the list; this is only the set of words
 * it may contain, so a role added there is refused here rather than printed as an unknown. */
export const MANDATORY_ROLE_NAMES = ["admin", "approver", "auditor"] as const;
export type MandatoryRole = (typeof MANDATORY_ROLE_NAMES)[number];

export interface MfaPolicy {
  require_mfa_for_all_members: boolean;
  mandatory_roles: MandatoryRole[];
  reauthentication_required: boolean;
}

function isMandatoryRole(value: unknown): value is MandatoryRole {
  return MANDATORY_ROLE_NAMES.some((name) => name === value);
}

export const mfaPolicyKey = ["workspace-mfa-policy"] as const;

async function policyFrom(response: Response, message: string): Promise<MfaPolicy> {
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) throw refusalFrom(response, body, message);
  if (
    !body ||
    typeof body !== "object" ||
    !("require_mfa_for_all_members" in body) ||
    typeof body.require_mfa_for_all_members !== "boolean" ||
    !("reauthentication_required" in body) ||
    typeof body.reauthentication_required !== "boolean" ||
    !("mandatory_roles" in body) ||
    !Array.isArray(body.mandatory_roles) ||
    body.mandatory_roles.length === 0 ||
    !body.mandatory_roles.every(isMandatoryRole)
  ) {
    throw new TypeError("The MFA policy response could not be read.");
  }
  return {
    require_mfa_for_all_members: body.require_mfa_for_all_members,
    mandatory_roles: body.mandatory_roles,
    reauthentication_required: body.reauthentication_required,
  };
}

export function useMfaPolicy(enabled: boolean) {
  return useQuery({
    queryKey: mfaPolicyKey,
    enabled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    queryFn: async () =>
      policyFrom(
        await apiFetch("/api/v1/org/security/mfa"),
        "This workspace's MFA policy could not be read.",
      ),
  });
}

export function useUpdateMfaPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (requireForEveryone: boolean) =>
      policyFrom(
        await apiFetch("/api/v1/org/security/mfa", {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ require_mfa_for_all_members: requireForEveryone }),
        }),
        "The MFA policy could not be saved.",
      ),
    onSuccess: async (policy) => {
      queryClient.setQueryData(mfaPolicyKey, policy);
      if (!policy.reauthentication_required) {
        await queryClient.invalidateQueries({ queryKey: ["administration"] });
      }
    },
  });
}

export function useMfaPolicySignIn() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.cancelQueries();
    queryClient.setQueryData(authKeys.me(), null);
    queryClient.removeQueries({
      predicate: (query) => JSON.stringify(query.queryKey) !== JSON.stringify(authKeys.me()),
    });
  };
}
